/* import __COLOCATED_TEMPLATE__ from './treemap.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import { use } from 'ember-resources/util/function-resource';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import type {
  SeriesTreemapOptions,
  Options,
  DataLabelsOptions,
  PointLabelObject,
  AxisLabelsFormatterContextObject,
  Point,
} from 'highcharts';
import { getNumericValue, type MetricValue } from 'embercom/lib/reporting/chart-data-resource';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import { inject as service } from '@ember/service';
import { colorForNoMetricValue, metricConfigs, type MetricId, addTopicFilter } from './common';

interface Args {
  reportState: ReportState;
  limit: number;
  metricId: MetricId;
  topicId: string | null;
  onTopicSelected: (topicId: string | null) => void;
}

export default class Treemap extends Component<Args> {
  @service declare reportingMetrics: ReportingMetrics;

  @use topicsData = staticDataResource(this, {
    serializedChart: () => this.chartDefinition,
    reportState: () => this.args.reportState,
  });

  get metricConfig() {
    return metricConfigs[this.args.metricId];
  }

  get chartDefinition() {
    if (this.args.topicId === null) {
      return this.topicsChartDefinition;
    }
    return this.subtopicsChartDefinition;
  }

  get topicsChartDefinition() {
    return {
      view_by: 'conversation_custom_fields#ai_insights_topic',
      view_by_display_limit: 500,
      chart_series: [{ metric_id: 'v1.new_conversations' }, this.metricConfig.seriesConfig],
    };
  }

  get subtopicsChartDefinition() {
    if (this.args.topicId === null) {
      throw new Error('topicId is required for subtopic chart definition');
    }
    return {
      view_by: 'conversation_custom_fields#ai_insights_topic_subtopic',
      view_by_display_limit: 500,
      chart_series: [
        addTopicFilter({ metric_id: 'v1.new_conversations' }, this.args.topicId),
        addTopicFilter(this.metricConfig.seriesConfig, this.args.topicId),
      ],
    };
  }

  get highchartsOptions(): Options {
    let component = this;
    return {
      chart: {
        margin: 0,
        marginBottom: 80,
      },
      colorAxis: {
        ...this.metricConfig.colorAxis,
        showInLegend: true,
        labels: {
          useHTML: true,
          step: 2,
          formatter(this: AxisLabelsFormatterContextObject) {
            return component.topicsData.formatValue(
              this.value as any,
              component.args.metricId,
              component.metricConfig.seriesConfig?.aggregation,
            );
          },
        },
      },
      tooltip: { enabled: false },
      plotOptions: {
        series: {
          point: {
            events: {
              click(this: Point & { id: string }) {
                if (component.args.topicId) {
                  component.args.onTopicSelected(null);
                } else {
                  component.args.onTopicSelected(this.id);
                }
              },
            },
          },
          states: {
            hover: {
              borderColor: 'white',
              brightness: 0.1,
            },
          },
        },
      },
    };
  }

  buildChartDataItem(id: string, metricValues: MetricValue[]) {
    let [volume, metricValue] = metricValues;
    let [topicName, subtopicName] = id.split('=>');
    let colorValue = getNumericValue(metricValue) ?? undefined;
    return {
      id,
      name: subtopicName ?? topicName,
      value: getNumericValue(volume) || 0,
      colorValue,
      metricValue,
      isSubtopic: subtopicName !== undefined,
    };
  }

  buildChartData(data: Record<string, MetricValue[]>) {
    return Object.entries(data)
      .map(([id, metricValues]) => this.buildChartDataItem(id, metricValues))
      .sort((a, b) => b.value - a.value);
  }

  get data() {
    return this.buildChartData(this.topicsData.flatCombinedData).slice(0, this.args.limit);
  }

  get highchartsSeries(): Array<
    SeriesTreemapOptions & {
      dataLabels: DataLabelsOptions;
    }
  > {
    let component = this;
    return [
      {
        type: 'treemap',
        color: colorForNoMetricValue,
        allowTraversingTree: true,
        turboThreshold: 0,
        animation: false,
        levels: [{ level: 1, borderWidth: 4, borderColor: 'white', dataLabels: { enabled: true } }],
        borderRadius: 4,
        dataLabels: {
          align: 'left',
          verticalAlign: 'top',
          padding: 12,
          style: {
            textOverflow: 'ellipsis',
            textOutline: '0',
            fontSize: '12px',
            fontWeight: 'normal',
          },
          enabled: true,
          crop: true,
          overflow: 'none' as any,
          formatter(this: PointLabelObject) {
            let formattedMetricValue = component.topicsData.formatValue(
              (this.point as any).metricValue,
              component.args.metricId,
              component.metricConfig.seriesConfig?.aggregation,
            );
            return `<b>${this.point.name}</b><br />${formattedMetricValue}`;
          },
        },
        layoutAlgorithm: 'strip',
        layoutStartingDirection: 'horizontal',
        data: this.data,
      },
    ];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::FinAiAgent::Analyze::TopicTrends::Treemap': typeof Treemap;
  }
}
